import React, { useContext, useMemo, useCallback } from "react";
import { useLocalization } from "@fluent/react";
import { TextInput, Select, DateInput } from "@narmi/design_system";
import { FREQUENCIES } from "byzantine/src/Recurrence";
import { DateTime } from "luxon";
import Account from "byzantine/src/Account";
import { useUserFeatures } from "../../../../contexts/UserFeaturesContext";
import { formAdapters } from "../../../../../src";
import AccountContext from "../../../../contexts/AccountContext";
import { TRANSLATED_FREQUENCIES } from "../../../FrequencySelector";
import InstitutionSettingsContext from "../../../../contexts/InstitutionSettingsContext";
import ComboAccountSelector from "../../../../ComboAccountSelector";
import { SudoProvider } from "../../../../SudoContext";
import SudoDialog from "../../../../SudoDialog";
import LimitsDialog from "../../../LimitsDialog";
import useTransferLimits from "../../hooks/useTransferLimits";
import type { LoanPaymentFormType } from "../types";

interface UserFeatures {
  loan_payment_by_external_ach?: boolean;
}

interface BankPaymentFieldsProps {
  form: LoanPaymentFormType;
  limits: any;
  basename?: string;
}

const BankPaymentFields = ({
  form,
  limits,
  basename = "loans",
}: BankPaymentFieldsProps) => {
  const { l10n } = useLocalization();
  const { accounts } = useContext(AccountContext) as { accounts: Account[] };
  const {
    disable_daily_transfer: disableDailyTransfer,
    disable_semimonthly_transfer: disableSemimonthlyTransfer,
  } = useContext(InstitutionSettingsContext);
  const { loan_payment_by_external_ach: allowPaymentByExternalAch = false } =
    useUserFeatures() as UserFeatures;

  const eligibleAccounts = useMemo(
    () =>
      accounts.filter((a: Account) => {
        if (a.isExternal() && !allowPaymentByExternalAch) {
          return false;
        }
        return a.isValidTransferSource();
      }),
    [accounts, allowPaymentByExternalAch]
  );

  const showAddExternalAccountLink = useMemo(() => {
    // isInternalLoan() covers all loan product types (loan, mortgage, lease, line_of_credit),
    // not just accounts with account_type "loan" -- credit-type loans (e.g. HELOCs) were being missed
    const loanAccounts = accounts.filter((a: Account) => a.isInternalLoan());
    return loanAccounts.some((account: Account) =>
      account.isValidAchTransferDestination()
    );
  }, [accounts]);

  // Process limits using custom hook
  const { limitsForDisplay } = useTransferLimits(limits);

  const frequencyOptions = useMemo(
    () =>
      Object.entries(FREQUENCIES)
        .filter(
          ([, value]) =>
            !(value === FREQUENCIES.DAILY && disableDailyTransfer) &&
            !(
              value === FREQUENCIES.FIRST_AND_FIFTEENTH &&
              disableSemimonthlyTransfer
            )
        )
        .map(([key, value]) => ({
          value: key,
          label: l10n.getString(TRANSLATED_FREQUENCIES[value]),
        })),
    [l10n, disableDailyTransfer, disableSemimonthlyTransfer]
  );

  const handleAccountChange = useCallback(
    (value: string) => {
      const selectedAccount = eligibleAccounts.find(
        (account) => account.id === value
      );

      if (selectedAccount) {
        const { source } = selectedAccount;

        form.setFieldValue("fromAccountId", value, true);

        form.setFieldValue(
          "paymentAccountName",
          selectedAccount.getShortDescription(true),
          true
        );

        if (source && source !== form.values.fromAccountSource) {
          form.setFieldValue("fromAccountSource", source, true);
        }
      }
    },
    [form, eligibleAccounts]
  );

  const isRecurring = form.values.frequency !== "ONCE";

  const dateLabel = l10n.getString(
    `label-date-${isRecurring ? "recurring-new" : "once"}`
  );

  return (
    <SudoProvider basename={basename}>
      <div className="margin--top--l">
        {/* From Account Selector */}
        <div style={{ marginBottom: "var(--space-m)" }}>
          <ComboAccountSelector
            label={l10n.getString("label-account")}
            accounts={eligibleAccounts}
            value={form.values.fromAccountId || ""}
            onChange={handleAccountChange}
            errorText={
              form.touched.fromAccountId && form.errors.fromAccountId
                ? form.errors.fromAccountId
                : ""
            }
            isDestination={false}
            showAddExternalAccountLink={showAddExternalAccountLink}
            basename={basename}
          />
        </div>

        {/* Amount Input (always shown, disabled for preset payments) */}
        <div style={{ marginBottom: "var(--space-m)" }}>
          <TextInput
            label={l10n.getString("label-amount")}
            inputMode="decimal"
            disabled={form.values.paymentType === "amountDue"}
            {...formAdapters.amountInput(form, "amount")}
          />
        </div>

        {/* Date and Frequency fields (only for non-principal payments and non-custom payments) */}
        {form.values.paymentType !== "principal" && (
          <>
            {/* Frequency Selector */}
            <div style={{ marginBottom: "var(--space-m)" }}>
              <Select
                label={l10n.getString("label-frequency")}
                {...formAdapters.select(form, "frequency")}
              >
                {frequencyOptions.map(({ value, label }) => (
                  <Select.Item key={value} value={value}>
                    {label}
                  </Select.Item>
                ))}
              </Select>
            </div>

            {/* Date Picker */}
            <div style={{ marginBottom: "var(--space-m)" }}>
              <DateInput
                minDate={
                  isRecurring
                    ? DateTime.now().plus({ days: 1 }).toFormat("MM/dd/yyyy")
                    : "today"
                }
                dateFormat={"m/d/Y"}
                altFormat={"m/d/Y"}
                label={dateLabel}
                useIsoOnChange={false}
                altInput
                disableMobile
                {...formAdapters.fieldWithOnChange(form, "date")}
              />
            </div>
          </>
        )}

        {/* Limits Dialog (only show for non-custom payments) */}
        <LimitsDialog limitsForDisplay={limitsForDisplay} />
      </div>
      <SudoDialog />
    </SudoProvider>
  );
};

export default BankPaymentFields;
